import React from 'react';
import { Link } from 'react-router-dom';

interface FooterProps {
  language: 'tr' | 'en';
}

const Footer: React.FC<FooterProps> = ({ language }) => {
  const currentYear = new Date().getFullYear();

  const content = {
    tr: {
      description: 'Modern teknolojiler ile işinizi büyütün. Web geliştirme, mobil uygulama ve dijital çözümler ile markanızı dijital dünyada öne çıkarın.',
      services: 'Hizmetlerimiz',
      contact: 'İletişim',
      servicesList: [
        'UyumTakip'
      ],
      copyright: '© {year} CG Teknoloji. Tüm hakları saklıdır.',
      legal: {
        privacy: 'Gizlilik Politikası',
        terms: 'Kullanım Şartları',
        kvkk: 'KVKK'
      },
      legalInfoTitle: 'Yasal Bilgiler',
      legalLinks: [
        { label: 'Çerez Politikası', href: '/tr/about/certification/cerez' },
        { label: 'Gizlilik Politikası ve Kişisel Veriler', href: '/tr/about/certification/gizlilikveri' },
        { label: 'Tedarikçi ve Müşterilerin Sözleşme Süreçlerine Yönelik KVKK Aydınlatma Metni', href: '/tr/about/certification/kvkkaydinlatma' },
        { label: 'Veri Sorumlusuna Başvuru Formu', href: '/tr/about/certification/verisorumlusubasvuru' },
        { label: 'Gizlilik Sözleşmesi Ve Kullanım Hakları', href: '/tr/about/certification/gizlilikkullanim' },
        { label: 'Bilgi Toplumu Hizmetleri', href: '/tr/about/certification/bilgitoplumu' },
      ],
    },
    en: {
      description: 'Grow your business with modern technologies. Stand out in the digital world with web development, mobile applications and digital solutions.',
      services: 'Our Services',
      contact: 'Contact',
      servicesList: [
        'UyumTakip'
      ],
      copyright: '© {year} CG Teknoloji. All rights reserved.',
      legal: {
        privacy: 'Privacy Policy',
        terms: 'Terms of Use',
        kvkk: 'GDPR'
      },
      legalInfoTitle: 'Legal Information',
      legalLinks: [
        { label: 'Cookie Policy', href: '/en/about/certification/cerez' },
        { label: 'Privacy Policy & Personal Data', href: '/en/about/certification/gizlilikveri' },
        { label: 'KVKK Disclosure for Supplier & Customer Contracts', href: '/en/about/certification/kvkkaydinlatma' },
        { label: 'Data Controller Application Form', href: '/en/about/certification/verisorumlusubasvuru' },
        { label: 'Confidentiality Agreement & Usage Rights', href: '/en/about/certification/gizlilikkullanim' },
        { label: 'Information Society Services', href: '/en/about/certification/bilgitoplumu' },
      ],
    }
  };

  const currentContent = content[language];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-2xl font-bold text-white mb-4">CG Teknoloji</h3>
            <p className="text-gray-300 mb-6 max-w-md">
              {currentContent.description}
            </p>
            <div className="flex space-x-4">
              <a href="https://uyumtakip.com/" className="text-gray-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">UyumTakip</span>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18 15 15 0 010-18z" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/uyum-takip/" className="text-gray-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/uyum.takip/" className="text-gray-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{currentContent.services}</h3>
            <ul className="space-y-2 text-gray-300">
              {currentContent.servicesList.map((service) => {
                const slugMap: Record<string, string> = {
                  'UyumTakip': 'uyumtakip',
                  'Fiyat Borsası': 'fiyat-borsasi',
                  'Dijital Rafineri': 'dijital-rafineri',
                };
                const slug = slugMap[service];
                return (
                  <li key={service}>
                    {slug ? (
                      <Link to={`/${language}/investment/${slug}`} className="hover:text-white transition-colors">
                        {service}
                      </Link>
                    ) : (
                      <span className="hover:text-white transition-colors">{service}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{currentContent.contact}</h3>
            <ul className="space-y-2 text-gray-300">
              <li>info@cgteknoloji.com</li>
              <li><a href="tel:4445860" className="hover:text-white transition-colors">444 5 860</a></li>
              <li>{language === 'tr' ? 'Üsküdar, İstanbul, Türkiye' : 'Üsküdar, Istanbul, Turkey'}</li>
            </ul>
          </div>

          {/* Legal Information */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 text-left">{currentContent.legalInfoTitle}</h3>
            <ul className="space-y-2 text-gray-300 text-left">
              {currentContent.legalLinks.map((item) => (
                <li key={item.label} className="w-full">
                  {item.href === '#' ? (
                    <button type="button" className="hover:text-white transition-colors text-left w-full">
                      {item.label}
                    </button>
                  ) : (
                    <a href={item.href} className="hover:text-white transition-colors text-left block w-full">
                      {item.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              {currentContent.copyright.replace('{year}', currentYear.toString())}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 